import { FamilyDeviceTokenError } from './errors';
/**
 * family-device — API.
 *
 * Endpoints used by a browser linked as a "family device" (profile picker +
 * Netflix-style student login). Authenticated by the x-family-device-token
 * header — NOT by JWT — so this uses raw fetch instead of the `http` helper
 * (whose 401 handling would wrongly log the auth session out).
 *
 * A 401 here means the device token was revoked/expired: it is cleared so the
 * browser falls back to the landing page.
 */
import { getBackendApiUrl } from '../../config/environment';
import {
  clearFamilyDeviceToken,
  getFamilyDeviceToken,
} from '../../services/family-device-storage';
import type { BackendLoginResponse } from '../../services/authentication/backend-auth-provider';
import type { FamilyDeviceProfilesResponse } from './types';

/** Thrown when the stored device token is missing, revoked or expired */

async function deviceFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getFamilyDeviceToken();
  if (!token) {
    throw new FamilyDeviceTokenError('No family device token on this browser');
  }

  const response = await fetch(`${getBackendApiUrl()}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      'x-family-device-token': token,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });

  if (response.status === 401) {
    // Token revoked or expired — unlink this browser
    clearFamilyDeviceToken();
    throw new FamilyDeviceTokenError();
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const message =
      (data && typeof data.message === 'string' && data.message) ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }
  return data as T;
}

export const familyDeviceApi = {
  /** Lists the family's student + guardian profiles for the picker */
  getProfiles: () => deviceFetch<FamilyDeviceProfilesResponse>('/family/device/profiles'),

  /** Logs in as a student profile; the device token is the credential */
  profileLogin: (studentUserId: string) =>
    deviceFetch<BackendLoginResponse>('/family/device/profile-login', {
      method: 'POST',
      body: JSON.stringify({ studentUserId }),
    }),

  /** Emails the guardian's 6-digit access code (their only way in) */
  requestGuardianCode: (guardianUserId: string, locale?: string) =>
    deviceFetch<{ maskedEmail: string; resendAvailableAt: string }>(
      '/family/device/request-guardian-code',
      {
        method: 'POST',
        body: JSON.stringify({ guardianUserId, locale }),
      },
    ),

  /** Signs in as a guardian by typing their emailed access code */
  guardianCodeLogin: (guardianUserId: string, code: string) =>
    deviceFetch<BackendLoginResponse>('/family/device/guardian-code-login', {
      method: 'POST',
      body: JSON.stringify({ guardianUserId, code }),
    }),

  /** Unlinks (revokes) this device — used by the picker's confirmed logout */
  unlink: () =>
    deviceFetch<{ message: string }>('/family/device/unlink', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};
